'use client';

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useConsent } from '../../contexts/ConsentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePlayer } from '../../contexts/PlayerContext';
import { ExpandedPanel } from './ExpandedPanel';
import { MiniButton } from './MiniButton';

const FloatingContainer = styled.div<{ $offset?: number }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  pointer-events: none;
  transform: ${({ $offset = 0 }) => ($offset > 0 ? `translateY(-${$offset}px)` : 'none')};
  will-change: transform;

  @media (max-width: 480px) {
    bottom: 12px;
    right: 12px;
    left: 12px;
    align-items: flex-end;
  }
`;

const HiddenIframe = styled.iframe`
  position: fixed;
  top: -9999px;
  left: -9999px;
  width: 300px;
  height: 166px;
  opacity: 0.01;
  pointer-events: none;
  border: 0;
  z-index: -999;
`;

const SC_API_SCRIPT = 'https://w.soundcloud.com/player/api.js';
const SC_WIDGET_BASE = 'https://w.soundcloud.com/player/?';
const DEFAULT_SC_URL = 'https://soundcloud.com/joevnoise/tough-love-joevnoise-sailorandi-tribute';
const DEFAULT_SC_TITLE = 'Tough Love (Sailor & I Tribute)';

function buildEmbedUrl(soundUrl: string): string {
  const params = new URLSearchParams({
    url: soundUrl,
    color: '#00ff00',
    auto_play: 'false',
    hide_related: 'true',
    show_comments: 'false',
    show_user: 'false',
    show_reposts: 'false',
    show_teaser: 'false',
    visual: 'false',
    buying: 'false',
    liking: 'false',
    download: 'false',
    sharing: 'false',
  });
  return `${SC_WIDGET_BASE}${params.toString()}`;
}

export function unlockAudioContext(): void {
  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (AudioCtx) {
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
    }
  } catch {
    // Ignore audio context error
  }
}

/**
 * Modular SoundCloud floating audio player connecting to SoundCloud widget SDK.
 * Keeps iframe DOM src static to prevent cross-origin document reloads on track switches.
 */
export const SoundCloudPlayer: React.FC = () => {
  const {
    currentTrackUrl,
    currentTrackTitle,
    currentTrackIndex,
    isPlaying,
    isAutoPlayEnabled,
    queue,
    playNext,
    playPrev,
    togglePlay,
    toggleAutoPlay,
    isMinimized,
    expandPlayer,
    hidePlayer,
    playTrack,
    registerIframe,
    widgetRef,
  } = usePlayer();

  const { preferences, requestSoundCloudPermission } = useConsent();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const apiLoadedRef = useRef(false);
  const [progress, setProgress] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [soundTitle, setSoundTitle] = useState<string | null>(null);
  const [footerOffset, setFooterOffset] = useState(0);
  const prevUrlRef = useRef<string | null>(null);
  const isExpanded = !isMinimized;
  const containerRef = useRef<HTMLDivElement>(null);

  // Dynamically float above footer when scrolled to the bottom of the page
  useEffect(() => {
    let rafId: number | null = null;

    const updateOffset = () => {
      const footer = document.querySelector('footer');
      if (!footer) {
        setFooterOffset(0);
        return;
      }
      const rect = footer.getBoundingClientRect();
      const visibleFooter = window.innerHeight - rect.top;
      setFooterOffset(Math.max(0, visibleFooter));
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        updateOffset();
        rafId = null;
      });
    };

    updateOffset();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    let observer: ResizeObserver | null = null;
    const footerEl = document.querySelector('footer');
    if (footerEl && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => handleScroll());
      observer.observe(footerEl);
    }

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      observer?.disconnect();
    };
  }, []);

  // Constant embed URL for the iframe element to avoid unloads
  const initialEmbedUrlRef = useRef<string>(buildEmbedUrl(DEFAULT_SC_URL));

  const shouldMountIframe = preferences.soundCloud && (Boolean(currentTrackUrl) || isPlaying);

  useEffect(() => {
    registerIframe(iframeRef.current);
  }, [registerIframe, shouldMountIframe]);

  const currentTrackUrlRef = useRef(currentTrackUrl);
  currentTrackUrlRef.current = currentTrackUrl;

  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;

  const isAutoPlayEnabledRef = useRef(isAutoPlayEnabled);
  isAutoPlayEnabledRef.current = isAutoPlayEnabled;

  const currentTrackIndexRef = useRef(currentTrackIndex);
  currentTrackIndexRef.current = currentTrackIndex;

  // Load SC Widget API script once iframe is mounted
  useEffect(() => {
    if (apiLoadedRef.current || !iframeRef.current || !shouldMountIframe) return;

    const init = () => {
      const sc = window.SC;
      if (!sc || !iframeRef.current) return;
      const widget = sc.Widget(iframeRef.current);
      widgetRef.current = widget;

      widget.bind(sc.Widget.Events.READY, () => {
        apiLoadedRef.current = true;

        // Listen for buffer loading progress
        widget.bind(sc.Widget.Events.LOAD_PROGRESS, (data: unknown) => {
          if (data && typeof data === 'object') {
            const d = data as { loadedProgress?: number };
            if (typeof d.loadedProgress === 'number') {
              setBuffered(Math.min(Math.max(d.loadedProgress * 100, 0), 100));
            }
          }
        });

        // Listen for progress updates
        widget.bind(sc.Widget.Events.PLAY_PROGRESS, (data: unknown) => {
          if (data && typeof data === 'object') {
            const d = data as {
              relativePosition?: number;
              currentPosition?: number;
              loadedProgress?: number;
            };
            if (typeof d.relativePosition === 'number') {
              setProgress(Math.min(Math.max(d.relativePosition * 100, 0), 100));
            }
            if (typeof d.currentPosition === 'number') {
              setCurrentTimeMs(d.currentPosition);
            }
            if (typeof d.loadedProgress === 'number') {
              setBuffered(Math.min(Math.max(d.loadedProgress * 100, 0), 100));
            }
            setIsBuffering(false);
          }
        });

        // Listen for seek updates
        widget.bind(sc.Widget.Events.SEEK, (data: unknown) => {
          if (data && typeof data === 'object') {
            const d = data as {
              relativePosition?: number;
              currentPosition?: number;
              loadedProgress?: number;
            };
            if (typeof d.relativePosition === 'number') {
              setProgress(Math.min(Math.max(d.relativePosition * 100, 0), 100));
            }
            if (typeof d.currentPosition === 'number') {
              setCurrentTimeMs(d.currentPosition);
            }
            if (typeof d.loadedProgress === 'number') {
              setBuffered(Math.min(Math.max(d.loadedProgress * 100, 0), 100));
            }
            setIsBuffering(false);
          }
        });

        // Listen for track finished to auto-advance
        widget.bind(sc.Widget.Events.FINISH, () => {
          setProgress(0);
          setCurrentTimeMs(0);
          setIsBuffering(false);
          if (isAutoPlayEnabledRef.current) {
            playNext();
          }
        });

        // Listen for track changes inside a set
        widget.bind(sc.Widget.Events.PLAY, () => {
          setIsBuffering(false);
          widget.getCurrentSound((sound) => {
            if (sound?.title) setSoundTitle(sound.title);
            if (typeof sound?.duration === 'number' && sound.duration > 0) {
              setDurationMs(sound.duration);
            }
          });
          widget.getDuration((d) => {
            if (typeof d === 'number' && d > 0) setDurationMs(d);
          });
        });

        widget.bind(sc.Widget.Events.PAUSE, () => {
          setIsBuffering(false);
        });

        // If a specific track was requested before READY fired, load it now
        const targetUrl = currentTrackUrlRef.current;
        if (targetUrl && targetUrl !== DEFAULT_SC_URL) {
          prevUrlRef.current = targetUrl;
          setIsBuffering(true);
          widget.load(targetUrl, {
            auto_play: isPlayingRef.current,
            callback: () => {
              setProgress(0);
              setBuffered(0);
              setCurrentTimeMs(0);
              setIsBuffering(false);
              if (typeof currentTrackIndexRef.current === 'number') {
                try {
                  widget.skip(currentTrackIndexRef.current);
                } catch {
                  // Handled by widget
                }
              }
              if (isPlayingRef.current) {
                widget.play();
              }
              widget.getCurrentSound((sound) => {
                if (sound?.title) setSoundTitle(sound.title);
                if (typeof sound?.duration === 'number' && sound.duration > 0) {
                  setDurationMs(sound.duration);
                }
              });
              widget.getDuration((d) => {
                if (typeof d === 'number' && d > 0) setDurationMs(d);
              });
            },
          });
        } else if (isPlayingRef.current) {
          try {
            widget.play();
          } catch {
            // Handled by browser interaction
          }
        }
      });
    };

    if (window.SC) {
      init();
      return;
    }

    const script = document.createElement('script');
    script.src = SC_API_SCRIPT;
    script.async = true;
    script.onload = init;
    document.body.appendChild(script);
  }, [widgetRef, shouldMountIframe]);

  // Handle dynamic track changes via widget.load without changing iframe src
  useEffect(() => {
    if (!currentTrackUrl) return;
    if (prevUrlRef.current === currentTrackUrl) return;
    prevUrlRef.current = currentTrackUrl;

    if (widgetRef.current && apiLoadedRef.current) {
      setIsBuffering(true);
      setProgress(0);
      setBuffered(0);
      setCurrentTimeMs(0);
      widgetRef.current.load(currentTrackUrl, {
        auto_play: true,
        callback: () => {
          setIsBuffering(false);
          setProgress(0);
          if (typeof currentTrackIndex === 'number') {
            try {
              widgetRef.current?.skip(currentTrackIndex);
            } catch {
              // Handled by widget
            }
          }
          widgetRef.current?.play();
          widgetRef.current?.getCurrentSound((sound) => {
            if (sound?.title) setSoundTitle(sound.title);
            if (typeof sound?.duration === 'number' && sound.duration > 0) {
              setDurationMs(sound.duration);
            }
          });
          widgetRef.current?.getDuration((d) => {
            if (typeof d === 'number' && d > 0) setDurationMs(d);
          });
        },
      });
    }
  }, [currentTrackUrl, currentTrackIndex, widgetRef]);

  // Handle play / pause synchronization
  useEffect(() => {
    if (!widgetRef.current || !apiLoadedRef.current) return;
    widgetRef.current.isPaused((paused) => {
      if (isPlaying && paused) widgetRef.current?.play();
      if (!isPlaying && !paused) widgetRef.current?.pause();
    });
  }, [isPlaying, widgetRef]);

  const displayTitle = soundTitle ?? currentTrackTitle ?? DEFAULT_SC_TITLE;

  const handleTogglePlay = () => {
    unlockAudioContext();
    if (!preferences.soundCloud) {
      const allowed = requestSoundCloudPermission(
        currentTrackUrl || DEFAULT_SC_URL,
        displayTitle,
        () => {
          unlockAudioContext();
          if (!currentTrackUrl) {
            playTrack(DEFAULT_SC_URL, DEFAULT_SC_TITLE);
          } else {
            togglePlay();
          }
        }
      );
      if (!allowed) return;
    }

    if (!currentTrackUrl) {
      playTrack(DEFAULT_SC_URL, DEFAULT_SC_TITLE);
    } else {
      togglePlay();
    }
  };

  const handleSeek = useCallback(
    (pctOrEvent: number | React.MouseEvent<HTMLDivElement>) => {
      if (!widgetRef.current) return;
      let pct: number;
      if (typeof pctOrEvent === 'number') {
        pct = pctOrEvent;
      } else {
        const rect = pctOrEvent.currentTarget.getBoundingClientRect();
        pct = ((pctOrEvent.clientX - rect.left) / rect.width) * 100;
      }
      const clampedPct = Math.min(Math.max(pct, 0), 100);
      setProgress(clampedPct);

      widgetRef.current.getDuration((ms) => {
        if (typeof ms === 'number' && ms > 0) {
          setDurationMs(ms);
          const targetMs = Math.round((clampedPct / 100) * ms);
          setCurrentTimeMs(targetMs);
          widgetRef.current?.seekTo(targetMs);
        }
      });
    },
    [widgetRef]
  );

  return (
    <>
      {shouldMountIframe && (
        <HiddenIframe
          ref={iframeRef}
          id="sc-widget-iframe"
          src={initialEmbedUrlRef.current}
          allow="autoplay; encrypted-media; fullscreen; picture-in-picture; unload"
          title="SoundCloud Player"
        />
      )}

      <FloatingContainer ref={containerRef} $offset={footerOffset}>
        <ExpandedPanel
          isExpanded={isExpanded}
          displayTitle={displayTitle}
          progress={progress}
          buffered={buffered}
          isBuffering={isBuffering}
          currentTimeMs={currentTimeMs}
          durationMs={durationMs}
          isPlaying={isPlaying}
          isAutoPlayEnabled={isAutoPlayEnabled}
          currentTrackUrl={currentTrackUrl}
          queue={queue}
          onClose={hidePlayer}
          onTogglePlay={handleTogglePlay}
          onToggleAutoPlay={toggleAutoPlay}
          onSeek={handleSeek}
          onPlayNext={playNext}
          onPlayPrev={playPrev}
          onSelectTrack={(track) => {
            unlockAudioContext();
            if (!preferences.soundCloud) {
              const allowed = requestSoundCloudPermission(track.soundcloudUrl, track.title, () => {
                unlockAudioContext();
                playTrack(track.soundcloudUrl, track.title);
              });
              if (!allowed) return;
            }
            playTrack(track.soundcloudUrl, track.title);
          }}
        />

        <MiniButton
          isPlaying={isPlaying}
          isExpanded={isExpanded}
          displayTitle={displayTitle}
          onToggleExpand={() => (isExpanded ? hidePlayer() : expandPlayer())}
          onTogglePlay={handleTogglePlay}
        />
      </FloatingContainer>
    </>
  );
};

export default SoundCloudPlayer;
